import type { AssetManager } from "../runtime/assetManager";

/**
 * HTMLAudio ベースのシンプルな音響バックエンド。
 * Phase 1 MVP: BGM (ループ + クロスフェード) + SE (16 slot) + Voice (チャンネル別)
 */
export class AudioBackend {
  private bgm: HTMLAudioElement | null = null;
  private bgmName: string | null = null;
  private seSlots = new Map<number, HTMLAudioElement>();
  private voiceChannels = new Map<number, HTMLAudioElement>();

  private bgmVolume = 0.8;
  private seVolume = 0.8;
  private voiceVolume = 1.0;
  private masterMute = false;

  constructor(private assets: AssetManager) {}

  setMasterMute(muted: boolean): void {
    this.masterMute = muted;
    this.applyAllVolumes();
  }

  setBgmVolume(v: number): void {
    this.bgmVolume = clamp01(v);
    if (this.bgm) this.bgm.volume = this.effective(this.bgmVolume);
  }

  setSeVolume(v: number): void {
    this.seVolume = clamp01(v);
    for (const a of this.seSlots.values()) a.volume = this.effective(this.seVolume);
  }

  setVoiceVolume(v: number): void {
    this.voiceVolume = clamp01(v);
    for (const a of this.voiceChannels.values()) a.volume = this.effective(this.voiceVolume);
  }

  // -------------------------- BGM

  async playBgm(name: string, opts: Record<string, unknown> = {}): Promise<void> {
    if (name === "" || name === "__volume__") {
      const target = Number(opts["to"] ?? 80) / 100;
      this.setBgmVolume(target);
      return;
    }
    if (this.bgmName === name && this.bgm && !this.bgm.paused) return;
    const url = this.assets.getBgmUrl(name);
    if (!url) {
      console.warn(`BGM not found in manifest: ${name}`);
      return;
    }
    if (this.bgm) {
      // 既存をフェードアウト
      const old = this.bgm;
      this.bgm = null;
      this.bgmName = null;
      fadeOut(old, 300).then(() => old.pause());
    }
    const audio = new Audio(url);
    audio.loop = true;
    const volPct = Number(opts["vol"] ?? 80);
    audio.volume = this.effective(this.bgmVolume * (volPct / 100));
    this.bgm = audio;
    this.bgmName = name;
    try {
      await audio.play();
    } catch (e) {
      // 自動再生ブロック — ユーザー操作後にリトライさせる
      console.warn("BGM auto-play blocked, will retry on user input:", e);
      this.scheduleUnlock();
    }
  }

  async stopBgm(opts: Record<string, unknown> = {}): Promise<void> {
    const fadeMs = Number(opts["fadeout"] ?? 0.5) * 1000;
    if (!this.bgm) return;
    const old = this.bgm;
    this.bgm = null;
    this.bgmName = null;
    await fadeOut(old, fadeMs);
    old.pause();
  }

  // -------------------------- SE

  async playSe(slot: number, name: string, opts: Record<string, unknown> = {}): Promise<void> {
    const url = this.assets.getSeUrl(name);
    if (!url) {
      console.warn(`SE not found in manifest: ${name}`);
      return;
    }
    this.stopSe(slot);
    const audio = new Audio(url);
    const volPct = Number(opts["vol"] ?? 100);
    audio.volume = this.effective(this.seVolume * (volPct / 100));
    const loop = Number(opts["loop"] ?? 0);
    if (loop !== 0) audio.loop = true;
    this.seSlots.set(slot, audio);
    try {
      await audio.play();
    } catch {
      /* ignore */
    }
  }

  async stopSe(slot: number): Promise<void> {
    const a = this.seSlots.get(slot);
    if (!a) return;
    a.pause();
    this.seSlots.delete(slot);
  }

  // -------------------------- Voice

  async playVoice(channel: number, name: string, opts: Record<string, unknown> = {}): Promise<void> {
    const url = this.assets.getVoiceUrl(name);
    if (!url) {
      console.warn(`Voice not found in manifest: ${name}`);
      return;
    }
    this.stopVoice(channel);
    const audio = new Audio(url);
    const volPct = Number(opts["vol"] ?? 100);
    audio.volume = this.effective(this.voiceVolume * (volPct / 100));
    this.voiceChannels.set(channel, audio);
    try {
      await audio.play();
    } catch {
      /* ignore */
    }
  }

  async stopVoice(channel: number): Promise<void> {
    const a = this.voiceChannels.get(channel);
    if (!a) return;
    a.pause();
    this.voiceChannels.delete(channel);
  }

  // -------------------------- 内部

  private effective(v: number): number {
    return this.masterMute ? 0 : Math.max(0, Math.min(1, v));
  }

  private applyAllVolumes(): void {
    if (this.bgm) this.bgm.volume = this.effective(this.bgmVolume);
    for (const a of this.seSlots.values()) a.volume = this.effective(this.seVolume);
    for (const a of this.voiceChannels.values()) a.volume = this.effective(this.voiceVolume);
  }

  private unlockScheduled = false;
  private scheduleUnlock(): void {
    if (this.unlockScheduled) return;
    this.unlockScheduled = true;
    const handler = () => {
      this.unlockScheduled = false;
      window.removeEventListener("pointerdown", handler);
      if (this.bgm && this.bgm.paused) {
        this.bgm.play().catch(() => {});
      }
    };
    window.addEventListener("pointerdown", handler, { once: true });
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

async function fadeOut(audio: HTMLAudioElement, ms: number): Promise<void> {
  if (ms <= 0) {
    audio.volume = 0;
    return;
  }
  const start = audio.volume;
  const steps = Math.max(1, Math.floor(ms / 40));
  for (let i = 1; i <= steps; i++) {
    audio.volume = Math.max(0, start * (1 - i / steps));
    await sleep(ms / steps);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
